"use client";

import { useEffect, useState } from "react";
import { GameType } from "@/lib/types";
import { useRouter } from "next/navigation";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { PlayIcon } from "lucide-react";
import "./loading-bar.css";
import Script from "next/script";

// vendor prefix 메서드를 포함하는 인터페이스 정의
interface Fullscreenable extends HTMLDivElement {
  webkitRequestFullscreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
}

interface GamePlayerProps {
  game: GameType;
}

export function GamePlayer({ game }: GamePlayerProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Log the game play for recommendation tracking when the game has started
    if (hasStarted) {
      logGamePlay(game.id);
    }
  }, [hasStarted, game.id]);

  // Function to log game play for recommendation tracking
  const logGamePlay = async (gameId: string) => {
    try {
      await fetch('/api/user/log-game-play', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gameId })
      });
      
      // No need to handle the response - this is a background tracking operation
      console.log('Game play logged for recommendations');
    } catch (error) {
      // Just log the error, don't disrupt the user experience
      console.error('Failed to log game play for recommendations:', error);
    }
  };

  const handleStartGame = () => {
    setHasStarted(true);
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-bar">
          <div className="progress"></div>
        </div>
        <div className="mt-2 text-center text-sm">게임 로딩 중...</div>
      </div>
    );
  }

  if (!hasStarted) {
    return (
      <Card className="w-full relative overflow-hidden game-start-card">
        <CardContent className="p-8 flex flex-col items-center justify-center gap-4 text-center h-[70vh]">
          <h2 className="text-3xl font-bold">{game.title}</h2>
          <p className="text-gray-600">{game.description}</p>
          <div className="mt-4">
            <Button 
              onClick={handleStartGame}
              size="lg" 
              className="px-8 py-6 rounded-full flex items-center gap-2 start-game-button"
            >
              <PlayIcon size={24} />
              <span className="text-lg">게임 시작하기</span>
            </Button>
          </div>
        </CardContent>
        <style jsx global>{`
          .game-start-card {
            background: #f9f4f0;
            box-shadow: 8px 8px 16px #d9d4d0, 
                        -8px -8px 16px #ffffff;
            border-radius: 16px;
            border: none;
          }
          
          .start-game-button {
            background: linear-gradient(45deg, #ff7b00, #ff9a00);
            color: white;
            font-weight: bold;
            border: none;
            transition: all 0.3s ease;
            box-shadow: 0 4px 10px rgba(255, 123, 0, 0.3);
          }
          
          .start-game-button:hover {
            transform: translateY(-3px);
            box-shadow: 0 6px 15px rgba(255, 123, 0, 0.4);
          }
          
          .start-game-button:active {
            transform: translateY(0);
            box-shadow: 0 2px 5px rgba(255, 123, 0, 0.4);
          }
        `}</style>
      </Card>
    );
  }

  function base64Encode(gameUrl: string): string | undefined {
    return Buffer.from(gameUrl).toString('base64');
  }

  return (
    <div className="game-container">
      {game.gameUrl.includes('.swf') ? 
      game.isVerified ? (
        <embed
          src={game.gameUrl}
          title={game.title}
          className="w-full h-[70vh] border-0 rounded-md shadow-lg"
        />) : (
          <>
           <iframe
        src={`https://player.kidszzanggame.com/waflash/code.php?gameUrl=${base64Encode(game.gameUrl)}`}
        title={game.title}
        className="w-full h-[70vh] border-0 rounded-md shadow-lg"
        allowFullScreen
        />
        
          </>
      ) : (
        <iframe
        src={game.gameUrl}
        title={game.title}
        className="w-full h-[70vh] border-0 rounded-md shadow-lg"
        allowFullScreen
        />
      )}
    </div>
  );
}
